// Previews a calendar click before the user makes it.
// In the date-range dialog, pointing at a day, week, or month temporarily highlights
// the range a click would select and shows its Start/End dates. The dates already
// selected in the dialog (the draft) stay unchanged until the user actually clicks.
import { PointerEvent, useEffect, useRef } from 'react';
import { PeriodOption, Resolution } from '../report-settings/reportSettings.ts';
import {
    DraftRange,
    getHoverIntent,
    getHoverRange,
    toCompleteRange,
    toDraftRange,
    toVisualRange,
} from './draftRange.ts';
import { PeriodIndexRange } from './periodRanges.ts';
import {
    hideHoverTip,
    parsePeriodIndex,
    setVisualRange,
    updateAllCellClasses,
    updateBoundaryLabels,
    updateHoverTipPosition,
    updateHoverTipText,
} from './rangePreview.ts';


export function useRangePreview(draft: DraftRange, periods: PeriodOption[], resolution: Resolution) {
    const cellRefs = useRef(new Map<number, HTMLElement>());
    const previewRange = useRef<PeriodIndexRange | null>(toCompleteRange(draft));
    const startValueRef = useRef<HTMLElement>(null);
    const endValueRef = useRef<HTMLElement>(null);
    const hoverTipRef = useRef<HTMLDivElement>(null);
    const hoveredPeriodIndex = useRef<number | null>(null);
    const hoverTipFrame = useRef<number | null>(null);
    const hoverTipPosition = useRef({ x: 0, y: 0 });

    // After a calendar click or a Days/Weeks/Months change, show the dialog's selected
    // dates instead of the dates that were being previewed under the pointer.
    useEffect(() => {
        previewRange.current = toVisualRange(draft);
        updateAllCellClasses(cellRefs.current, previewRange.current);
        updateBoundaryLabels(startValueRef.current, endValueRef.current, draft, periods, resolution);
    }, [draft, periods, resolution]);

    useEffect(() => () => {
        if (hoverTipFrame.current != null) cancelAnimationFrame(hoverTipFrame.current);
    }, []);

    function handlePointerOver(event: PointerEvent<HTMLDivElement>) {
        const target = (event.target as HTMLElement).closest<HTMLElement>('[data-period-index]');
        const periodIndex = parsePeriodIndex(target);
        if (periodIndex == null) {
            resetPreview();
            return;
        }
        if (periodIndex === hoveredPeriodIndex.current) return;
        hoveredPeriodIndex.current = periodIndex;

        const nextRange = getHoverRange(draft, periodIndex);
        setVisualRange(cellRefs.current, previewRange.current, nextRange);
        previewRange.current = nextRange;
        updateBoundaryLabels(
            startValueRef.current,
            endValueRef.current,
            toDraftRange(nextRange, draft),
            periods,
            resolution,
        );
        updateHoverTipText(hoverTipRef.current, getHoverIntent(draft, periodIndex));
    }

    function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
        // Move the instruction tooltip with the pointer. Several pointer events can
        // arrive before the screen is painted, so use only the latest position per frame.
        hoverTipPosition.current = { x: event.clientX, y: event.clientY };
        if (hoverTipFrame.current != null) return;
        hoverTipFrame.current = requestAnimationFrame(() => {
            hoverTipFrame.current = null;
            updateHoverTipPosition(
                hoverTipRef.current,
                hoverTipPosition.current.x,
                hoverTipPosition.current.y,
            );
        });
    }

    function resetPreview() {
        // When the pointer leaves the calendar, remove its suggested range and restore
        // the dates selected by clicks. A start without an end remains incomplete.
        hoveredPeriodIndex.current = null;
        const selectedRange = toVisualRange(draft);
        setVisualRange(cellRefs.current, previewRange.current, selectedRange);
        previewRange.current = selectedRange;
        updateBoundaryLabels(startValueRef.current, endValueRef.current, draft, periods, resolution);
        hideHoverTip(hoverTipRef.current);
    }

    function clearCells() {
        cellRefs.current.clear();
    }

    function clearPreview() {
        clearCells();
        previewRange.current = null;
    }

    function registerCell(periodIndex: number, element: HTMLElement | null) {
        if (element) cellRefs.current.set(periodIndex, element);
        else cellRefs.current.delete(periodIndex);
    }

    return {
        startValueRef,
        endValueRef,
        hoverTipRef,
        handlePointerOver,
        handlePointerMove,
        resetPreview,
        clearCells,
        clearPreview,
        registerCell,
    };
}
